import { type CSSProperties } from 'react';
import {
  type PhotoPrintButtonEvent,
  type PhotoPrintDraft,
  type PhotoPrintFilter,
  type PhotoPrintStep,
  type PhotoPrintTextStyle,
  PHOTO_PRINT_CAPTION_STYLE,
} from './PhotoPrintFlowTypes';

export function createInitialDraft(): PhotoPrintDraft {
  return {
    step: 'products',
    source: null,
    originalImage: null,
    editedImage: null,
    crop: {
      rotation: 0,
      flipped: false,
      aspectRatio: '2:3',
    },
    zoom: 1.2,
    filter: 'original',
    brightness: 5,
    contrast: 0,
    saturation: 10,
    caption: '',
    textStyle: PHOTO_PRINT_CAPTION_STYLE.HANDWRITING,
    sendWithoutText: false,
    quantity: 1,
    confirmed: false,
    isAddedToBasket: false,
    basketPhotoPrintId: null,
  };
}

export function normalizePhotoPrintQuantity(quantity: unknown) {
  const numericQuantity = Number(quantity);
  return Number.isFinite(numericQuantity) ? Math.max(1, numericQuantity) : 1;
}

export function stopPhotoPrintButtonEvent(event: PhotoPrintButtonEvent) {
  event.preventDefault();
  event.stopPropagation();
}

export function normalizeStepForTabs(step: PhotoPrintStep) {
  if (step === 'products' || step === 'source' || step === 'camera' || step === 'customizePrompt') {
    return 'source';
  }
  return step;
}

export function getFilterPreviewClass(filter: PhotoPrintFilter) {
  switch (filter) {
    case 'vintage':
      return 'sepia contrast-90 saturate-75';
    case 'warm':
      return 'sepia-[.25] saturate-150 contrast-105';
    case 'cool':
      return 'saturate-75 contrast-110 hue-rotate-15';
    case 'bw':
      return 'grayscale contrast-110';
    case 'original':
    default:
      return '';
  }
}

export function getPhotoImageStyle(draft?: PhotoPrintDraft): CSSProperties | undefined {
  if (!draft) return undefined;

  const filterParts = [
    getBaseFilterValue(draft.filter),
    `brightness(${100 + draft.brightness}%)`,
    `contrast(${100 + draft.contrast}%)`,
    `saturate(${100 + draft.saturation}%)`,
  ].filter(Boolean);

  return {
    transform: `scale(${draft.zoom}) rotate(${draft.crop.rotation}deg) ${
      draft.crop.flipped ? 'scaleX(-1)' : ''
    }`,
    objectFit: draft.crop.aspectRatio === 'fit' ? 'contain' : 'cover',
    filter: filterParts.join(' '),
  };
}

export function getTextStyleClass(style: PhotoPrintTextStyle) {
  switch (style) {
    case 'handwriting':
      return 'italic';
    case 'typewriter':
      return 'tracking-normal';
    case 'classic':
    default:
      return 'font-semibold';
  }
}

export function getTextStyleInline(style: PhotoPrintTextStyle): CSSProperties {
  switch (style) {
    case 'handwriting':
      return { fontFamily: 'Bradley Hand, Comic Sans MS, cursive' };
    case 'typewriter':
      return { fontFamily: 'Courier New, monospace' };
    case 'classic':
    default:
      return { fontFamily: 'Georgia, Times New Roman, serif' };
  }
}

function getBaseFilterValue(filter: PhotoPrintFilter) {
  switch (filter) {
    case 'vintage':
      return 'sepia(0.75) contrast(0.95) saturate(0.8)';
    case 'warm':
      return 'sepia(0.35) hue-rotate(-12deg) saturate(1.25)';
    case 'cool':
      return 'hue-rotate(20deg) saturate(0.85) contrast(1.08)';
    case 'bw':
      return 'grayscale(1)';
    case 'original':
    default:
      return '';
  }
}
